import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type {
  NetworkRisk,
  Risk,
  RiskFactorDetail,
  RiskOverrideInput,
} from "@/types/risk"

type RecordSubject = { type: "firm" | "individual"; id: string }

function riskPath(subject: RecordSubject) {
  return subject.type === "firm"
    ? `/firms/${subject.id}/risk`
    : `/individuals/${subject.id}/risk`
}

export const riskQueries = {
  subject: (subject: RecordSubject) =>
    queryOptions({
      queryKey: apiKey(riskPath(subject)),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<Risk>>(riskPath(subject), {
          signal,
        })

        return data
      },
    }),

  /** The network's word, what most firms share, and who to look at first. */
  network: () =>
    queryOptions({
      queryKey: apiKey("/risk"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<NetworkRisk>>("/risk", {
          signal,
        })

        return data
      },
    }),

  /** One factor and the firms it affects, searchable by name. */
  factor: (key: string, search?: string) =>
    queryOptions({
      queryKey: apiKey(`/risk/factors/${encodeURIComponent(key)}`, { search }),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<RiskFactorDetail>>(
          `/risk/factors/${encodeURIComponent(key)}`,
          { params: { search }, signal }
        )

        return data
      },
    }),
}

function useRiskWrite<TInput>(
  subject: RecordSubject,
  method: "PUT" | "DELETE",
  body: (input: TInput) => unknown
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: TInput) => {
      const { data } = await apiFetch<ApiResource<Risk>>(
        `${riskPath(subject)}/override`,
        { method, body: body(input) }
      )

      return data
    },
    onSuccess: (risk) => {
      queryClient.setQueryData(apiKey(riskPath(subject)), risk)
    },
  })
}

export function useOverrideRisk(subject: RecordSubject) {
  return useRiskWrite(subject, "PUT", (input: RiskOverrideInput) => input)
}

export function useClearRiskOverride(subject: RecordSubject) {
  return useRiskWrite(subject, "DELETE", () => undefined)
}
